import React from 'react';
import { Plus, Trash2, X, MessageSquare, Ghost, Zap } from 'lucide-react';
import { usePermission } from '../../services/PermissionService';
import { PERMISSION_MANAGE_SERVER } from '../../core/permissions/permissions';

interface ServerRailProps {
  vaults: any[];
  discordServers?: any[];
  activeVault: any;
  onJoinRoom: (vault: any) => void;
  onCreateRoom: () => void;
  currentUserId?: string;
  onDeleteVault: (vaultId: string) => void;
  onClearNodes?: () => void;
  onClearGuests?: () => void;
  activeTab: 'chat' | 'feed';
  setActiveTab: (tab: 'chat' | 'feed') => void;
  sidebarView: 'dms' | 'channels';
  setSidebarView: (view: 'dms' | 'channels') => void;
  setActiveVault: (vault: any) => void;
  mobileView?: 'sidebar' | 'chat';
  setMobileView?: (view: 'sidebar' | 'chat') => void;
  isCinemaMode?: boolean;
  contacts?: any[];
  setActiveContactId?: (id: string | null) => void;
  activeContactId?: string | null;
  onServerContextMenu?: (e: React.MouseEvent, vaultId: string) => void;
  vaultUnreadCounts?: Record<string, number>;
  onOpenDiscordAdmin?: () => void;
}


import { useLongPress } from '../../hooks/useLongPress';

function ServerRailItem({ vault, activeVault, activeTab, onJoinRoom, setSidebarView, setActiveTab, onContextMenu, unreadCount, isDiscord }: any) {
  const isActive = activeVault?.id === vault.id && activeTab !== 'feed';
  const isCreator = usePermission(PERMISSION_MANAGE_SERVER, { vaultId: vault.id });

  const longPressProps = useLongPress(
    (e) => {
      e.preventDefault();
      if (onContextMenu) {
        onContextMenu(e);
      }
    },
    (e) => {
      onJoinRoom(vault);
      setSidebarView('channels');
      setActiveTab('chat');
    },
    { delay: 500 }
  );

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(e);
    }
  };

  return (
    <div
      key={vault.id}
      className="relative group flex items-center justify-center w-12 h-12 shrink-0"
      title={vault.name}
      onContextMenu={handleContextMenu}
    >
      <div className={`absolute -left-1 w-1 rounded-r-full ${isDiscord ? 'bg-indigo-500' : 'bg-[var(--neon)]'} transition-all duration-200 ease-[var(--ease-soft)] shadow-[0_0_8px_var(--neon)] ${
        isActive ? 'h-8' : 'h-0 group-hover:h-5'
      }`} />

      <div
        className={`relative w-12 h-12 flex items-center justify-center overflow-hidden cursor-pointer transition-all duration-200 ease-[var(--ease-soft)] font-mono text-xs font-black tracking-wider uppercase bg-slate-900 border ${
          isActive
            ? (isDiscord
              ? 'border-indigo-500 text-indigo-400 shadow-[0_0_15px_rgba(79,70,229,0.5)] rounded-full scale-105'
              : 'border-[var(--neon)] text-[var(--neon)] shadow-[0_0_15px_var(--neon-glow)] rounded-full scale-105')
            : 'border-white/10 text-white/70 hover:border-white/30 active:scale-95 active:opacity-80 rounded-xl hover:rounded-full hover:scale-105'
        }`}
        {...longPressProps}
      >
        <img
          src={isDiscord ? (vault.discord_guild_icon || '/psydechat-icon-no-text.png') : ((vault.icon_url || vault.icon || vault.img) === '/Vault Icon.png' ? '/psydechat-icon-no-text.png' : (vault.icon_url || vault.icon || vault.img || '/psydechat-icon-no-text.png'))}
          alt={vault.name || vault.discord_guild_name}
          className="w-full h-full object-cover"
          style={
            !isDiscord && (!(vault.icon_url || vault.icon || vault.img) || ((vault.icon_url || vault.icon || vault.img) === '/Vault Icon.png') || (vault.icon_hue && !(vault.icon_url || vault.icon || vault.img).startsWith('http')))
              ? { filter: `hue-rotate(${vault.icon_hue || 0}deg)` }
              : undefined
          }
        />
        {isDiscord && (
          <div className="absolute top-0 right-0 bg-indigo-600 rounded-full p-0.5 border border-indigo-400 shadow-lg">
            <Zap size={8} className="text-white" />
          </div>
        )}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg border border-white/20 z-10 animate-in zoom-in">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServerRail({
  vaults,
  discordServers,
  activeVault,
  onJoinRoom,
  onCreateRoom,
  currentUserId,
  onDeleteVault,
  onClearNodes,
  onClearGuests,
  activeTab,
  setActiveTab,
  sidebarView,
  setSidebarView,
  setActiveVault,
  mobileView,
  setMobileView,
  isCinemaMode = false,
  contacts = [],
  activeContactId = null,
  setActiveContactId,
  onServerContextMenu,
  vaultUnreadCounts = {},
  onOpenDiscordAdmin,
}: ServerRailProps) {

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 3);
  };

  const isFeedActive = !activeVault && activeTab === 'feed';
  const isDMsActive = !activeVault && activeTab === 'chat';

  // Find contacts that have unread messages or unread counts
  const unreadContacts = React.useMemo(() => {
    return contacts.filter(c => c.unreadCount && c.unreadCount > 0);
  }, [contacts]);

  return (
    <aside className={`node-sidebar ${isCinemaMode ? 'hidden w-0 overflow-hidden' : `${mobileView === 'sidebar' ? 'flex' : 'hidden md:flex'} w-[72px]`} flex-col items-center gap-3 py-4 bg-black/50 border-r border-white/5 shrink-0 z-40`}>
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="psyde-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#4ade80" />
          </linearGradient>
        </defs>
      </svg>

      {/* Top Section: App Logo Toggle Button & Unread DMs */}
      <div className="flex flex-col items-center gap-2.5 w-full">
        {/* Interactive App Logo Button - Swaps between Global Feed and DM View */}
        <div className="relative group flex items-center justify-center w-12 h-12 shrink-0">
          <div className={`absolute -left-1 w-1 rounded-r-full bg-[var(--neon)] transition-all duration-200 ease-[var(--ease-soft)] ${
            (isFeedActive || isDMsActive) ? 'h-8' : 'h-0 group-hover:h-5'
          }`} />

          <button
            onClick={() => {
              setActiveVault(null);
              setSidebarView('dms');
              // Toggle between feed and chat (DMs) — always stay in sidebar on mobile
              if (activeTab === 'feed') {
                setActiveTab('chat');
              } else {
                setActiveTab('feed');
              }
            }}
            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ease-[var(--ease-soft)] cursor-pointer overflow-hidden p-1.5 ${
              (isFeedActive || isDMsActive)
                ? 'bg-slate-900 border border-[var(--neon)] text-[var(--neon)] shadow-[0_0_15px_var(--neon-glow)] rounded-full scale-105'
                : 'bg-black/40 border border-white/10 text-slate-400 hover:border-slate-700/50 hover:rounded-full rounded-2xl'
            }`}
            title={activeTab === 'feed' ? "Switch to Direct Messages" : "Switch to Global Feed"}
          >
            <img src="/psydechat-icon-no-text.png" alt="Psydechat" className="w-10 h-10 object-contain hover:scale-110 transition-transform" />
          </button>
        </div>

        {/* Unread DMs Rail (Renders avatar of contacts with unread messages) */}
        {unreadContacts.map(c => {
          const isSelected = !activeVault && activeContactId === c.id;
          return (
            // `cursor-pointer` on the wrapper so the affordance is consistent over the badge's ~4px
            // overhang past the button edge; the click itself still fires on the <button> below because
            // the badge has `pointer-events-none`.
            <div key={c.id} className="relative group flex items-center justify-center w-12 h-12 shrink-0 cursor-pointer">
              <div className={`absolute -left-1 w-1 rounded-r-full bg-emerald-400 transition-all duration-200 ease-[var(--ease-soft)] ${
                isSelected ? 'h-8' : 'h-3 group-hover:h-5'
              }`} />
              <button
                onClick={() => {
                  setActiveVault(null);
                  setSidebarView('dms');
                  setActiveTab('chat');
                  if (setActiveContactId) setActiveContactId(c.id);
                  if (setMobileView) setMobileView('chat');
                }}
                className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-all duration-200 ease-[var(--ease-soft)] relative ${
                  isSelected ? 'border-[var(--neon)] ring-4 ring-[var(--neon)]/20 scale-105' : 'border-transparent hover:border-emerald-400 hover:scale-105'
                }`}
                title={`Unread DM from ${c.name}`}
              >
                <img src={c.avatar} className="w-full h-full object-cover" alt={c.name} />
              </button>
              {c.unreadCount && c.unreadCount > 0 && (
                <span className="absolute -bottom-1 -right-1 bg-gradient-to-r from-slate-700 via-amber-400 to-yellow-400 text-slate-950 font-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center border-2 border-slate-900 shadow-md">
                  {c.unreadCount > 9 ? '9+' : c.unreadCount}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Divider */}
      <div className="w-8 h-[2px] bg-white/10 rounded my-1 shrink-0" />

      {/* Scrollable Server List */}
      <div className="flex-1 w-full flex flex-col items-center gap-2.5 overflow-y-auto no-scrollbar">
        {/* PSYDEChat Vaults - Filter out Discord-linked vaults to avoid duplication */}
        {(() => {
          // Get set of vault IDs that are linked to Discord servers
          const discordVaultIds = new Set(discordServers?.map(s => s.vault_id) || []);
          // Filter out Discord-linked vaults from regular vaults list
          return vaults.filter(v => !discordVaultIds.has(v.id));
        })().map((vault) => (
          <ServerRailItem
            key={vault.id}
            vault={vault}
            activeVault={activeVault}
            activeTab={activeTab}
            onJoinRoom={onJoinRoom}
            setSidebarView={setSidebarView}
            setActiveTab={setActiveTab}
            unreadCount={vaultUnreadCounts[vault.id] || 0}
            isDiscord={false}
            onContextMenu={(e: React.MouseEvent) => {
              if (onServerContextMenu) {
                onServerContextMenu(e, vault.id);
              }
            }}
          />
        ))}

        {/* Discord Servers - Use vault_id directly to ensure clicking works */}
        {discordServers && discordServers.length > 0 && (
          <>
            <div className="w-8 h-[2px] bg-indigo-500/30 rounded my-1 shrink-0" />
            {discordServers.map((server) => {
              // Find the actual vault from the vaults list or use a minimal object with vault_id
              const actualVault = vaults.find(v => v.id === server.vault_id);

              // If we have the actual vault, use it; otherwise construct a minimal one
              const vaultForClick = actualVault || {
                id: server.vault_id,
                name: server.vaults?.name || server.discord_guild_name,
                icon_url: server.vaults?.icon_url || server.discord_guild_icon,
                description: server.vaults?.description || `Discord: ${server.discord_guild_name}`,
                type: 'private',
                members: [],
              };

              return (
                <ServerRailItem
                  key={`discord-${server.discord_guild_id}`}
                  vault={vaultForClick}
                  activeVault={activeVault}
                  activeTab={activeTab}
                  onJoinRoom={onJoinRoom}
                  setSidebarView={setSidebarView}
                  setActiveTab={setActiveTab}
                  unreadCount={vaultUnreadCounts[vaultForClick.id] || 0}
                  isDiscord={true}
                  onContextMenu={(e: React.MouseEvent) => {
                    if (onServerContextMenu) {
                      onServerContextMenu(e, vaultForClick.id);
                    }
                  }}
                />
              );
            })}
          </>
        )}

        <div className="w-8 h-[2px] bg-white/10 rounded my-1 shrink-0" />

        <div className="relative group flex items-center justify-center w-12 h-12 shrink-0">
          <div className="absolute -left-1 w-1 rounded-r-full bg-white transition-all duration-200 ease-[var(--ease-soft)] h-0 group-hover:h-5" />
          <button
            className="w-12 h-12 rounded-2xl flex items-center justify-center bg-black/40 border border-white/10 text-white/50 hover:bg-white/10 hover:text-[var(--neon)] hover:border-[var(--neon)] hover:rounded-full transition-all duration-200 ease-[var(--ease-soft)] cursor-pointer shrink-0"
            onClick={onCreateRoom}
            title="Initialize New Server"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>


      {onClearNodes && (
        <button
          className="w-12 h-12 rounded-2xl flex items-center justify-center bg-black/40 border border-red-500/20 text-red-500/50 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500 hover:rounded-full transition-all duration-200 ease-[var(--ease-soft)] cursor-pointer mt-auto shrink-0 animate-pulse-slow"
          onClick={onClearNodes}
          title="Clear all active servers"
        >
          <Trash2 size={18} />
        </button>
      )}

      {onClearGuests && (
        <button
          className="w-12 h-12 rounded-2xl flex items-center justify-center bg-black/40 border border-orange-500/20 text-orange-500/50 hover:bg-orange-500/10 hover:text-orange-500 hover:border-orange-500 hover:rounded-full transition-all duration-200 ease-[var(--ease-soft)] cursor-pointer shrink-0 animate-pulse-slow"
          onClick={onClearGuests}
          title="Purge all guest (anonymous) accounts"
        >
          <Ghost size={18} />
        </button>
      )}

      {onOpenDiscordAdmin && (
        <button
          className="w-12 h-12 rounded-2xl flex items-center justify-center bg-black/40 border border-indigo-500/20 text-indigo-500/50 hover:bg-indigo-500/10 hover:text-indigo-500 hover:border-indigo-500 hover:rounded-full transition-all duration-200 ease-[var(--ease-soft)] cursor-pointer shrink-0"
          onClick={onOpenDiscordAdmin}
          title="Discord Bridge Admin"
        >
          <Zap size={18} />
        </button>
      )}
    </aside>
  );
}
